"use client";
import {
  Button,
  Card,
  ScreenLoader,
  SelectInput,
  SinglePairSelectorField,
} from "@/components";
import { MultiSelectField } from "@/components/ui/forms/MultiSelectField";
import { useFormStepTwo } from "@/hooks";
import { useOnboardingStore } from "@/stores";
import { OptionsJobber } from "@/types";
import Image from "next/image";
import { useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslations } from "next-intl";

type FormStepTwoProp = {
  optionsJobber: {
    options?: OptionsJobber;
    optionError: boolean;
    optionIsLoading: boolean;
  };
  setStep: (step: number) => void;
  userId: string;
  buttonText?: string;
};

export default function FormStepTwo({
  optionsJobber,
  setStep,
  userId,
  buttonText,
}: FormStepTwoProp) {
  const {
    methods,
    onSubmit,
    setSearchNameSpecialty,
    setSearchNameSkill,
    selectedSkills,
    selectedSpecialties,
    mappedSkillsData,
    mappedSpecialtiesData,
    isLoading,
    isFetchingNextPage,
    isLoadingSkills,
    isFetchingNextSkillsPage,
    hasNextPage,
    hasNextSkillsPage,
    fetchNextPage,
    fetchNextSkillsPage,
    handleCategoryBadgeClick,
    handleSkillSpecialtySelection,
    handleCategoryRemove,
  } = useFormStepTwo({ optionsJobber, setStep, userId });

  const t = useTranslations("jobberProfile.forms.stepTwo");
  const { formData } = useOnboardingStore();

  useEffect(() => {
    if (!formData.specialtiesSkills) return;
    methods.reset(formData.specialtiesSkills);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!formData.specialtiesSkills]);

  // Fallback hydration: if store didn't populate yet but we have normalized profileSetup data in localStorage/sessionStorage
  useEffect(() => {
    if (formData.specialtiesSkills) return; // already hydrated from store
    try {
      const raw = sessionStorage.getItem("profile_setup_prefill");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.specialties_skills && !methods.getValues("skills")?.length) {
        // Expect same shape as RPC (specialties_skills) -> map minimal fields expected by form
        const ss = parsed.specialties_skills;
        methods.reset({
          level_english: ss.level_english || { id: "", name: "" },
          languages: (ss.languages || []).map((l: any) => ({
            first_id: l.language_id,
            name_first: l.language,
            second_id: l.language_level_id,
            name_second: l.language_level,
          })),
          categories: ss.categories || [],
          skills: (ss.skills || []).map((s: any) => ({
            first_id: s.id,
            name_first: s.name,
            second_id: s.years_experience_id,
            name_second: s.years_experience,
            category_id: s.fk_category_id,
          })),
          specialties: (ss.specialties || []).map((s: any) => ({
            first_id: s.id,
            name_first: s.name,
            second_id: s.years_experience_id,
            name_second: s.years_experience,
            category_id: s.fk_category_id,
          })),
        });
      }
    } catch {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider {...methods}>
      {methods.formState.isSubmitting && <ScreenLoader />}
      <form className="pb-3 " onSubmit={methods.handleSubmit(onSubmit)}>
        <Card className="md:p-8 ">
          <div className="mb-6 bg-white border border-[#EDEEFE] rounded-2xl p-4">
            <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
              >
                <path
                  d="M12.0892 9.02529C12.4169 8.28409 12.5988 7.46408 12.5988 6.60156C12.5988 3.28785 9.9128 0.601562 6.59941 0.601562C3.28601 0.601562 0.599976 3.28785 0.599976 6.60156C0.599976 9.91527 3.28601 12.6016 6.59941 12.6016C7.66618 12.6016 8.66793 12.3231 9.53594 11.8349L12.6 12.601L12.0892 9.02529Z"
                  stroke="#151515"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {t("languages")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
              <SelectInput
                name="level_english"
                options={optionsJobber?.options?.language_level || []}
                loading={optionsJobber.optionIsLoading}
                label={t("englishLevel")}
                placeholder={t("englishLevelPlaceholder")}
                classNameContainer="w-full md:w-[298px]"
                noBorder
                noShadow
                className="bg-[#F9F9F9]"
              />
              <SinglePairSelectorField
                name="languages"
                label={t("addOtherLanguages")}
                loadingFirst={optionsJobber.optionIsLoading}
                firstOptions={
                  optionsJobber.options?.language.filter(
                    (e) => e.name != "English",
                  ) || []
                }
                showFilter={false}
                secondOptions={optionsJobber.options?.language_level || []}
                placeholderFirst={t("searchLanguage")}
                placeholderSecond={t("level")}
                noBorder
                noShadow
                bgStyleFirst="bg-[#F9F9F9]"
                bgStyleSecond="bg-[#F9F9F9]"
              />
            </div>
          </div>
          <div className="border-t-2 border-secondary-100 my-4"></div>

          <div className="flex items-center gap-2 px-4 py-3 bg-white border border-[#EDEEFE] rounded-xl mb-4">
            <div className="bg-purple-50 w-7 h-7 rounded-lg flex items-center justify-center shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="16"
                viewBox="0 0 10 16"
                fill="none"
              >
                <path
                  d="M3.2251 14.5996H6.7251M0.600098 4.97461C0.600098 2.55836 2.55885 0.599609 4.9751 0.599609C7.39134 0.599609 9.3501 2.55836 9.3501 4.97461C9.3501 6.76864 8.27027 8.237 6.7251 8.91211V11.5371C6.7251 12.0204 6.33335 12.4121 5.8501 12.4121H4.1001C3.61685 12.4121 3.2251 12.0204 3.2251 11.5371V8.98557C1.67993 8.31046 0.600098 6.76864 0.600098 4.97461Z"
                  stroke="url(#paint0_linear_tip_requirements)"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_tip_requirements"
                    x1="9.3501"
                    y1="-2.34942"
                    x2="-3.36377"
                    y2="10.4892"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#B71BFF" />
                    <stop offset="0.413462" stopColor="#8F36FF" />
                    <stop offset="0.889423" stopColor="#5B02FF" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <p className="text-sm text-black">
              {t("tipMessage")}{" "}
              <span
                className="font-normal"
                style={{
                  background:
                    "linear-gradient(90deg, #B71BFF 23.56%, #8F36FF 41.35%, #5B02FF 51.92%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {t("tipHighlight")}
              </span>{" "}
              {t("tipContinue")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left column: Categories + Skills (stacked) */}
            <div className="flex flex-col gap-4">
              <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
                <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <path
                      d="M8.90002 5.14841L5.544 8.14839L4.40002 7.12578M12.65 2.89842L12.65 10.3985C12.65 11.6411 11.6427 12.6484 10.4 12.6484H2.90002C1.65738 12.6484 0.650024 11.6411 0.650024 10.3985V2.89842C0.650024 1.65579 1.65738 0.648438 2.90002 0.648438H10.4C11.6427 0.648438 12.65 1.65579 12.65 2.89842Z"
                      stroke="#151515"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t("category")}
                </h3>
                <MultiSelectField
                  name="categories"
                  options={optionsJobber?.options?.category || []}
                  loading={optionsJobber.optionIsLoading}
                  placeholder={t("categoryPlaceholder")}
                  className="w-full md:w-[340px]"
                  showAllOption={true}
                  onBadgeClick={handleCategoryBadgeClick}
                  onBadgeRemove={handleCategoryRemove}
                  noBorder
                  noShadow
                  bgStyle="bg-[#F9F9F9]"
                />
              </div>
              <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
                <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <path
                      d="M8.90002 5.14841L5.544 8.14839L4.40002 7.12578M12.65 2.89842L12.65 10.3985C12.65 11.6411 11.6427 12.6484 10.4 12.6484H2.90002C1.65738 12.6484 0.650024 11.6411 0.650024 10.3985V2.89842C0.650024 1.65579 1.65738 0.648438 2.90002 0.648438H10.4C11.6427 0.648438 12.65 1.65579 12.65 2.89842Z"
                      stroke="#151515"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t("skills")}
                </h3>
                <SinglePairSelectorField
                  name="skills"
                  firstOptions={mappedSkillsData}
                  secondOptions={optionsJobber.options?.years_experiences || []}
                  onFirstSearch={setSearchNameSkill}
                  hasMoreFirst={!!hasNextSkillsPage}
                  onLoadMoreFirst={() => fetchNextSkillsPage()}
                  loadingFirst={isLoadingSkills || isFetchingNextSkillsPage}
                  placeholderFirst={t("skillsPlaceholder")}
                  placeholderSecond={t("years")}
                  syncCategory={true}
                  onCategoryChange={handleSkillSpecialtySelection}
                  categoryOptions={optionsJobber?.options?.category || []}
                  manualAdd
                  secondWidthClass="md:w-[115px]"
                  addButtonText={t("add")}
                  updateButtonText={t("update")}
                  noBorder
                  noShadow
                  bgStyleFirst="bg-[#F9F9F9]"
                  bgStyleSecond="bg-[#F9F9F9]"
                  key={`skills-${selectedSkills.length}`}
                />
              </div>
            </div>

            {/* Right column: Specialties (independent) */}
            <div className="flex flex-col gap-4">
              <div className="bg-white border border-[#EDEEFE] rounded-2xl p-4">
                <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <path
                      d="M8.90002 5.14841L5.544 8.14839L4.40002 7.12578M12.65 2.89842L12.65 10.3985C12.65 11.6411 11.6427 12.6484 10.4 12.6484H2.90002C1.65738 12.6484 0.650024 11.6411 0.650024 10.3985V2.89842C0.650024 1.65579 1.65738 0.648438 2.90002 0.648438H10.4C11.6427 0.648438 12.65 1.65579 12.65 2.89842Z"
                      stroke="#151515"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t("specialties")}
                </h3>
                <SinglePairSelectorField
                  name="specialties"
                  firstOptions={mappedSpecialtiesData}
                  secondOptions={optionsJobber.options?.years_experiences || []}
                  onFirstSearch={setSearchNameSpecialty}
                  hasMoreFirst={!!hasNextPage}
                  onLoadMoreFirst={() => fetchNextPage()}
                  loadingFirst={isLoading || isFetchingNextPage}
                  placeholderFirst={t("specialtiesPlaceholder")}
                  placeholderSecond={t("years")}
                  // IMPORTANT: Allow manual specialty addition even if no category picked yet.
                  // Previously syncCategory=true disabled the first select until a category was chosen,
                  // making it impossible to add specialties manually (user complaint).
                  // We disable category gating here; categories will still be backfilled asynchronously
                  // via the useFormStepTwo effect that looks up fk_category_id for selected specialties.
                  syncCategory={false}
                  // (Optional) If later we want conditional gating, we could introduce a separate flag.
                  manualAdd
                  allowNoSecond
                  secondWidthClass="md:w-[115px]"
                  addButtonText={t("add")}
                  updateButtonText={t("update")}
                  noBorder
                  noShadow
                  bgStyleFirst="bg-[#F9F9F9]"
                  bgStyleSecond="bg-[#F9F9F9]"
                  key={`specialties-${selectedSpecialties.length}`}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Action Button - Mobile/Desktop responsive like formStepOne */}
        <div className="mt-6 flex justify-center sm:justify-end">
          <Button
            type="button"
            rounded="rounded-xl"
            className="w-full sm:w-[208px]"
            isLoading={methods.formState.isSubmitting}
            onClick={async () => {
              // Force-touch fields so react-hook-form recognizes state even if unchanged
              try {
                // Ensure arrays default to [] to satisfy zod optional arrays (avoid undefined edge case)
                const v = methods.getValues();
                if (!v.skills)
                  methods.setValue("skills", [], { shouldDirty: false });
                if (!v.specialties)
                  methods.setValue("specialties", [], { shouldDirty: false });
                if (!v.categories)
                  methods.setValue("categories", [], { shouldDirty: false });
                if (!v.languages)
                  methods.setValue("languages", [], { shouldDirty: false });
                const valid = await methods.trigger(undefined, {
                  shouldFocus: true,
                });

                if (!valid) return;
                await methods.handleSubmit(onSubmit)();
              } catch (e) {
                // Silent fail
              }
            }}
          >
            {buttonText || t("next")}
          </Button>
        </div>

        {/* Profile completion message - Desktop only */}
        <div className="relative hidden md:block">
          <Image
            alt="ellipse"
            src={"/images/jobbi/women_profile.png"}
            width={0}
            height={0}
            sizes="100vw"
            className="absolute top-10 md:top-5 left-[-5px]  md:left-[-30px] w-[60px] md:max-w-[100px] h-auto"
            unoptimized
            priority
          />
          <div className="bg-brand-25 rounded-2xl p-4 max-w-lg text-center text-xs text-black">
            {t("profileCompletionMessage")}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
